import {useMemo, useState} from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap'
import {
  CampaignChannel,
  ICampaign,
  ICampaignLog,
  ICampaignTarget,
  IMessageTemplate,
} from '../../../types/campaigns'
import {ChannelBadge} from './ChannelBadge'
import clsx from 'clsx'

type Props = {
  campaign?: ICampaign | null
  targets: ICampaignTarget[]
  logs: ICampaignLog[]
  loadingTargets?: boolean
  loadingLogs?: boolean
  onEditCampaign: () => void
  onAddTarget: () => void
  onLaunchCampaign: () => void
  onPauseCampaign: () => void
  launching?: boolean
  pausing?: boolean
  onSendTest: (contactId: string, channels: CampaignChannel[]) => Promise<void>
  onEditTemplate: (template: IMessageTemplate) => void
  onRefreshTargets: () => void
  onRefreshLogs: () => void
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Not set'
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const channelOptions: CampaignChannel[] = ['SMS', 'EMAIL', 'WHATSAPP', 'TELEGRAM']

export const CampaignDetail = ({
  campaign,
  targets,
  logs,
  loadingTargets,
  loadingLogs,
  onEditCampaign,
  onAddTarget,
  onLaunchCampaign,
  onPauseCampaign,
  launching,
  pausing,
  onSendTest,
  onEditTemplate,
  onRefreshTargets,
  onRefreshLogs,
}: Props) => {
  const [testContactId, setTestContactId] = useState('')
  const [selectedChannels, setSelectedChannels] = useState<CampaignChannel[]>(['EMAIL'])
  const [sendingTest, setSendingTest] = useState(false)

  const enabledChannels = useMemo(() => {
    const raw = campaign?.enabledChannels as unknown
    if (Array.isArray(raw)) return raw as CampaignChannel[]
    if (typeof raw === 'string') {
      return raw
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean) as CampaignChannel[]
    }
    return []
  }, [campaign?.enabledChannels])

  const stats = useMemo(() => {
    if (!campaign) return []
    return [
      {label: 'Targets', value: campaign.totalTargets},
      {label: 'Sent', value: campaign.sentCount},
      {label: 'Delivered', value: campaign.deliveredCount},
      {label: 'Failed', value: campaign.failedCount},
    ]
  }, [campaign])

  const toggleChannel = (channel: CampaignChannel) => {
    setSelectedChannels((prev) => {
      if (prev.includes(channel)) {
        return prev.filter((item) => item !== channel)
      }
      return [...prev, channel]
    })
  }

  const handleSendTest = async () => {
    if (!testContactId || selectedChannels.length === 0) return
    setSendingTest(true)
    try {
      await onSendTest(testContactId, selectedChannels)
      setTestContactId('')
    } finally {
      setSendingTest(false)
    }
  }

  if (!campaign) {
    return (
      <div className='campaigns-detail card h-100'>
        <div className='card-body d-flex align-items-center justify-content-center text-muted'>
          Select a campaign to see its details
        </div>
      </div>
    )
  }

  return (
    <div className='campaigns-detail'>
      <div className='campaigns-detail__header card mb-5'>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-start flex-wrap gap-4'>
            <div>
              <h3 className='mb-1'>{campaign.name}</h3>
              <div className='text-muted'>{campaign.description || 'No description added'}</div>
              <div className='mt-3 d-flex flex-wrap gap-2'>
                {enabledChannels.map((channel) => (
                  <ChannelBadge
                    key={`${campaign.id}-${channel}`}
                    channel={channel as CampaignChannel}
                  />
                ))}
              </div>
            </div>
            <div className='d-flex gap-2'>
              <Button variant='light' onClick={onEditCampaign}>
                Edit campaign
              </Button>
              <Button variant='secondary' onClick={onAddTarget}>
                Add target
              </Button>
              {campaign.status === 'ACTIVE' ? (
                <Button variant='warning' onClick={onPauseCampaign} disabled={pausing}>
                  {pausing && <Spinner animation='border' size='sm' className='me-2' />}
                  Pause
                </Button>
              ) : (
                <Button variant='success' onClick={onLaunchCampaign} disabled={launching}>
                  {launching && <Spinner animation='border' size='sm' className='me-2' />}
                  Launch
                </Button>
              )}
            </div>
          </div>
          <div className='mt-4'>
            <div className='d-flex flex-wrap gap-4 text-gray-700'>
              <span>
                <strong>Status:</strong> {campaign.status}
              </span>
              <span>
                <strong>Schedule:</strong> {campaign.scheduleType}
              </span>
              <span>
                <strong>Next run:</strong> {formatDate(campaign.scheduledTime)}
              </span>
              <span>
                <strong>Last run:</strong> {formatDate(campaign.lastRunAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Row className='g-5 mb-5'>
        <Col xl={8}>
          <Card>
            <Card.Header>
              <Card.Title>Performance</Card.Title>
              <div className='text-muted fs-7'>Delivery metrics refresh after each execution</div>
            </Card.Header>
            <Card.Body>
              <Row className='g-4'>
                {stats.map((stat) => (
                  <Col md={3} key={stat.label}>
                    <div className='campaigns-stat'>
                      <div className='campaigns-stat__label'>{stat.label}</div>
                      <div className='campaigns-stat__value'>{stat.value ?? 0}</div>
                    </div>
                  </Col>
                ))}
              </Row>
              {/* {campaign.statistics && (
                <div className='mt-4'>
                  <div className='text-muted mb-2'>Raw statistics</div>
                  <pre className='campaigns-json'>{JSON.stringify(campaign.statistics, null, 2)}</pre>
                </div>
              )} */}
            </Card.Body>
          </Card>
          <Card className='mt-5'>
            <Card.Header>
              <Card.Title>Targets</Card.Title>
              <div className='card-toolbar d-flex gap-2'>
                <Button size='sm' variant='light' onClick={() => onRefreshTargets()}>
                  Refresh
                </Button>
                <Button size='sm' onClick={() => onAddTarget()}>
                  Add target
                </Button>
              </div>
            </Card.Header>
            <Card.Body className='p-0'>
              {loadingTargets ? (
                <div className='campaigns-placeholder'>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Loading targets
                </div>
              ) : targets.length === 0 ? (
                <div className='campaigns-placeholder'>No targets added yet</div>
              ) : (
                <div className='table-wrapper'>
                  <div className='table-responsive table table-striped table-hover '>
                    <Table
                      hover
                      className='table-scrollable table-scroll  table table-striped table-hover '
                      style={{whiteSpace: 'nowrap'}}
                    >
                      <thead>
                        <tr>
                          <th>Contact</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Status</th>
                          <th>Last channel</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {targets.map((target) => (
                          <tr key={target.id}>
                            <td>
                              {`${target.contact.firstName ?? ''} ${
                                target.contact.lastName ?? ''
                              }`.trim() || target.contact.id}
                            </td>
                            <td>{target.contact.email || '—'}</td>
                            <td>{target.contact.phone || '—'}</td>
                            <td>
                              <span
                                className={clsx(
                                  'badge',
                                  `badge-light-${target.state === 'FAILED' ? 'danger' : 'primary'}`
                                )}
                              >
                                {target.state}
                              </span>
                            </td>
                            <td>
                              {target.lastChannel ? (
                                <ChannelBadge channel={target.lastChannel} />
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>{target.errorCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
          <Card className='mt-5'>
            <Card.Header>
              <Card.Title>Activity log</Card.Title>
              <Button size='sm' variant='light' onClick={onRefreshLogs}>
                Refresh
              </Button>
            </Card.Header>
            <Card.Body className='p-0'>
              {loadingLogs ? (
                <div className='campaigns-placeholder'>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Loading logs
                </div>
              ) : logs.length === 0 ? (
                <div className='campaigns-placeholder'>No logs found for this campaign</div>
              ) : (
                <div className='table-wrapper'>
                  <div className='table-responsive table table-striped table-hover '>
                    <Table
                      hover
                      className='table-scrollable table-scroll  table table-striped table-hover '
                      style={{whiteSpace: 'nowrap'}}
                    >
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Channel</th>
                          <th>Status</th>
                          <th>Recipient</th>
                          <th>Provider ID</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr key={log.id}>
                            <td>{formatDate(log.createdAt)}</td>
                            <td>
                              <ChannelBadge channel={log.channel} />
                            </td>
                            <td>
                              <span
                                className={clsx(
                                  'badge',
                                  log.status === 'FAILED'
                                    ? 'badge-light-danger'
                                    : 'badge-light-success'
                                )}
                              >
                                {log.status}
                              </span>
                            </td>
                            <td>
                              {log.contact
                                ? `${log.contact.firstName ?? ''} ${
                                    log.contact.lastName ?? ''
                                  }`.trim() ||
                                  log.contact.email ||
                                  log.contact.id
                                : '—'}
                            </td>
                            <td>{log.providerMessageId || '—'}</td>
                            <td>
                              {log.errorMessage ? (
                                <OverlayTrigger
                                  overlay={
                                    <Tooltip id={`log-error-${log.id}`}>{log.errorMessage}</Tooltip>
                                  }
                                >
                                  <span className='text-danger'>View</span>
                                </OverlayTrigger>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4}>
          {campaign.template ? (
            <Card className='mb-5'>
              <Card.Header>
                <Card.Title>Template</Card.Title>
                <Button
                  size='sm'
                  variant='light'
                  onClick={() => onEditTemplate(campaign.template! as IMessageTemplate)}
                >
                  Edit template
                </Button>
              </Card.Header>
              <Card.Body>
                <div className='fw-bold'>{campaign.template?.name}</div>
                <div className='text-muted mb-3'>Channel: {campaign.template?.channel}</div>
                {campaign.template?.subject && (
                  <div className='mb-2'>Subject: {campaign.template.subject}</div>
                )}
                <div className='text-gray-600 small'>
                  {campaign.template?.description || 'No description provided'}
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Alert variant='light' className='mb-5'>
              This campaign has no template. Assign one before launching.
            </Alert>
          )}
          <Card className='mb-5'>
            <Card.Header>
              <Card.Title>Send test message</Card.Title>
            </Card.Header>
            <Card.Body>
              <Form.Group className='mb-3'>
                <Form.Label>Contact ID</Form.Label>
                <Form.Control
                  value={testContactId}
                  placeholder='e.g. contact database ID'
                  onChange={(event) => setTestContactId(event.target.value)}
                />
              </Form.Group>
              <div className='mb-3'>
                <div className='fw-semibold mb-2'>Channels</div>
                <div className='d-flex flex-wrap gap-3'>
                  {channelOptions.map((channel) => (
                    <Form.Check
                      type='checkbox'
                      key={channel}
                      label={channel}
                      checked={selectedChannels.includes(channel)}
                      onChange={() => toggleChannel(channel)}
                    />
                  ))}
                </div>
              </div>
              <Button
                variant='primary'
                disabled={!testContactId || selectedChannels.length === 0 || sendingTest}
                onClick={handleSendTest}
              >
                {sendingTest && <Spinner animation='border' size='sm' className='me-2' />}
                Send test
              </Button>
            </Card.Body>
          </Card>
          {/* <Card>
            <Card.Header>
              <Card.Title>Metadata</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className='text-muted mb-2'>Target segment</div>
              <pre className='campaigns-json mb-4'>
                {JSON.stringify(campaign.targetSegment ?? {}, null, 2)}
              </pre>
              <div className='text-muted mb-2'>Campaign metadata</div>
              <pre className='campaigns-json'>
                {JSON.stringify(campaign.metadata ?? {}, null, 2)}
              </pre>
            </Card.Body>
          </Card> */}
        </Col>
      </Row>
    </div>
  )
}
